import { Stack, Typography, useTheme } from "@mui/material";

import {
  AllergenChipOutlined,
  ChevronRightIcon,
  PreferenceChipOutlined,
} from "igfd-ui/src";

interface ProductListItemProps {
  title: string;
  subtitle?: string;
  preference?: string;
  allergenCount: number;
  hasBorder: boolean;
  onClick?: () => void;
}

/**
 * ProductListItem displays a dish's name, optional subtitle, and indicators (allergens, preference, arrow).
 * Layout: Left = info (title, subtitle), Right = indicators (allergen, preference, chevron).
 */
export const ProductListItem = ({
  title,
  subtitle,
  preference,
  allergenCount,
  hasBorder,
  onClick,
}: ProductListItemProps) => {
  const {
    palette: { oscar },
  } = useTheme();
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      onClick={onClick}
      sx={{
        borderBottom: hasBorder
          ? `1px solid ${oscar.light.borders.gray}`
          : "none",
        cursor: onClick ? "pointer" : "default",
        py: 1.25,
      }}
    >
      {/* Left cluster: Title and optional subtitle, vertically aligned */}
      <Stack direction="column" gap={0.25} justifyContent="center">
        <Typography variant="labelM">{title}</Typography>
        {/* Subtitle is optional and appears below the title if present */}
        {subtitle && (
          <Typography
            variant="labelSMedium"
            sx={{ color: oscar.light.content.content2 }}
          >
            {subtitle}
          </Typography>
        )}
      </Stack>
      {/* Right cluster: Allergen count, dietary preference, chevron arrow */}
      <Stack direction="row" alignItems="center" gap={1.5} minWidth={0}>
        {allergenCount > 0 && (
          <AllergenChipOutlined allergenName={allergenCount.toString()} />
        )}
        {preference && <PreferenceChipOutlined preferenceName={preference} />}
        {onClick && (
          <ChevronRightIcon
            color={oscar.light.content.link}
            height={16}
            width={16}
            style={{ position: "relative", top: -1 }}
          />
        )}
      </Stack>
    </Stack>
  );
};
